'use client';

import React, { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

export type ToastType = 'success' | 'error';

export interface ToastProps {
    message: string;
    type?: ToastType;
    duration?: number;
    onClose?: () => void;
}

export const Toast: React.FC<ToastProps> = ({
    message,
    type = 'success',
    duration = 3000,
    onClose
}) => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        // Trigger animation
        const rafId = requestAnimationFrame(() => {
            setIsVisible(true);
        });

        // Auto-hide after duration
        const timer = setTimeout(() => {
            setIsVisible(false);
            setTimeout(() => {
                onClose?.();
            }, 300); // Wait for fade-out animation
        }, duration);

        return () => clearTimeout(timer);
    }, [duration, onClose]);

    return (
        <div
            className={cn(
                'fixed top-6 left-1/2 -translate-x-1/2 z-[100]',
                'flex items-center gap-3',
                'bg-white text-black',
                'px-6 py-3.5',
                'rounded-full',
                'shadow-lg',
                'transition-all duration-300 ease-in-out',
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
            )}
        >
            {/* Message */}
            <span className="text-base font-medium whitespace-nowrap">
                {message}
            </span>

            {/* Icon with brand gradient border */}
            <div className="relative flex items-center justify-center w-8 h-8">
                {/* Gradient border using pseudo-element pattern */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-brand-purple to-brand-orange p-[2px]">
                    <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                        {type === 'success' ? (
                            <span
                                className="text-lg leading-none font-bold bg-gradient-to-r from-brand-purple to-brand-orange bg-clip-text text-transparent"
                            >
                                ✓
                            </span>
                        ) : (
                            <span
                                className="text-lg leading-none font-bold bg-gradient-to-r from-brand-purple to-brand-orange bg-clip-text text-transparent"
                            >
                                ✕
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
